import React, { useCallback, useMemo } from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";
import DataTable from "bundles/Internal/components/common/DataTable";
import InternalActivitiesTableFilters from "bundles/Internal/components/internal_activities/InternalActivitiesTableFilters";

export const COLUMNS_CONSTANTS = [
  { key: "created_at", label: "When" },
  { key: "internal_user", label: "User" },
  { key: "action", label: "Action" },
  { key: "trackable", label: "Target" },
];

const NAVIGABLE_TYPES = new Set(["Candidate", "Recruiter", "Company", "ProfilePipeline", "BlogPost", "JobApplication", "Credit"]);

const ACTION_COLOR = {
  created: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  updated: { bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  deleted: { bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  whitelisted: { bg: "rgba(123,31,162,0.10)", text: "#7B1FA2" },
  blacklisted: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

const TRACKABLE_TYPE_COLOR = {
  Candidate: { bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  Recruiter: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  Company: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  ProfilePipeline: { bg: "rgba(123,31,162,0.10)", text: "#7B1FA2" },
  ExternalProfile: { bg: "rgba(69,90,100,0.10)", text: "#455A64" },
  JobApplication: { bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      borderRadius: "6px",
      height: 22,
      textTransform: "capitalize",
    }}
  />
);

const FULL_DATE_FORMAT = new Intl.DateTimeFormat("fr-BE", {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit",
});

const relativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return FULL_DATE_FORMAT.format(date);
};

function formatActivityCell(activity, key) {
  const value = activity?.[key];

  if (key === "created_at") {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return (
      <Tooltip title={FULL_DATE_FORMAT.format(date)} placement="top" arrow>
        <Box component="span" sx={{ cursor: "default", color: "rgba(42,51,78,0.6)", fontSize: 12 }}>
          {relativeTime(value)}
        </Box>
      </Tooltip>
    );
  }

  if (key === "action") {
    if (!value) return "";
    return pill(value, ACTION_COLOR[value] || ACTION_COLOR.created);
  }

  if (key === "internal_user") {
    const user = activity?.internal_user;
    if (!user) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={user} size={28} />
        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {user.full_name || "Unknown"}
        </Box>
      </Box>
    );
  }

  if (key === "trackable") {
    const trackable = activity?.trackable;
    if (!trackable) return "";
    const typeColor = TRACKABLE_TYPE_COLOR[trackable.type] || TRACKABLE_TYPE_COLOR.Candidate;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {pill(trackable.type, typeColor)}
        <Tooltip title={trackable.label || ""} placement="top" arrow disableHoverListener={!trackable.label || trackable.label.length < 25}>
          <Box
            component="span"
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              maxWidth: 180,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
            }}
          >
            {trackable.label}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  if (value === null || value === undefined) return "";
  return String(value);
}

const InternalActivityTable = ({
  activities = [],
  onQueryChange,
  onActivityClick,
  filters,
  onFiltersChange,
  users = [],
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) => {
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);

  const renderCell = useCallback(
    (activity, key) => formatActivityCell(activity, key),
    [],
  );

  const handleRowClick = useCallback(
    (activity) => {
      if (NAVIGABLE_TYPES.has(activity?.trackable?.type)) onActivityClick?.(activity);
    },
    [onActivityClick],
  );

  const getRowSx = useCallback(
    (activity) => NAVIGABLE_TYPES.has(activity?.trackable?.type) ? {} : { cursor: "default" },
    [],
  );

  return (
    <DataTable
      title="Internal Activities"
      totalLabel={(count) => `${count.toLocaleString()} total activities`}
      emptyIcon={HistoryIcon}
      emptyTitle="No activities found"
      emptySubtitle="Try adjusting your search or filters"
      columns={columns}
      data={activities}
      renderCell={renderCell}
      onRowClick={handleRowClick}
      getRowSx={getRowSx}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by action, type..."
      searchInfo="Action, type, metadata"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <InternalActivitiesTableFilters filters={filters} onFiltersChange={onFiltersChange} users={users} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
    />
  );
};

export default InternalActivityTable;
